import React, { useState, useEffect } from "react";
import { Drawer, Form, Button, Select, message, Space, Alert, Row, Col, Checkbox, Modal, Tag, Input, Tooltip, Segmented } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import AttachmentUpload from "../../../../components/EmailEditor/components/AttachmentUpload";
import EditableFileList from "./EditableFileList";
import FileSplitModal from "./FileSplitModal";
import { settlementService } from "../../../../api/enhancedApi";
import { businessPartnerApi } from "../../../../api/contractManagementApi";
import { uploadFile } from "../../../../api/attachmentApi";
import { STANDARD_COST_TYPE_OPTIONS } from "../../constants";
import AirportSelector from "../../../../components/common/AirportSelector";

import { pdfjs } from "react-pdf";
import mammoth from "mammoth";

// 配置 PDF.js worker (与 FileSplitModal 保持一致)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ========== V4.6 Word 支持配置 ==========
// Word 文件字符数限制阈值（与 PDF 10页限制对应，约等于5000字/页）
const WORD_CHAR_LIMIT_HARD = 50000; // 硬限制：超过此值阻止上传
const WORD_CHAR_LIMIT_SOFT = 25000; // 软限制：超过此值警告

// 费用类型选项统一从常量获取，确保与列表和显示一致
const COST_TYPE_OPTIONS = STANDARD_COST_TYPE_OPTIONS;

// localStorage key：记住上次成功提交的表单字段值
const UPLOAD_DRAWER_LAST_VALUES_KEY = "invoice_upload_drawer_last_values";

const InvoiceDataSourceUploadDrawer = ({ open, onClose, onSuccess }) => {
    const [form] = Form.useForm();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedFileInfos, setUploadedFileInfos] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);

    // 上传模式：INVOICE_UPLOAD(账单上传) | PARTNER_BILLING(合作方账单)
    const [uploadMode, setUploadMode] = useState("INVOICE_UPLOAD");
    const isPartnerBilling = uploadMode === "PARTNER_BILLING";

    // V4.9 新增：合作方（CUSTOMER）列表
    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);

    // 文件关联组状态
    const [fileGroups, setFileGroups] = useState([]);

    // 文件拆分状态
    const [splittingFile, setSplittingFile] = useState(null); // 正在拆分的文件
    const [isSplitModalVisible, setIsSplitModalVisible] = useState(false);

    // 拖拽状态
    const [isDragging, setIsDragging] = useState(false);

    // ========== V4.5 新增 ==========
    // 临时存储页数信息（用于上传前检查和上传后附加）
    const pendingPageCountRef = React.useRef({});

    // 批量检查结果Modal状态
    const [batchCheckModalVisible, setBatchCheckModalVisible] = useState(false);
    const [batchCheckResult, setBatchCheckResult] = useState(null); // { passed, blocked, warnings, needsSplit }
    const batchCheckResolveRef = React.useRef(null); // 用于Promise resolve

    // ========== V4.7 新增：批次号推荐 ==========
    const generateRecommendedBatchNumber = () => {
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10).replace(/-/g, "");
        const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, "");
        return `BATCH_${dateStr}_${timeStr}`;
    };

    // 加载供应商列表 & 恢复上次提交的表单值
    useEffect(() => {
        if (open) {
            loadSuppliers();
            loadCustomers();
            // 从 localStorage 恢复上次成功提交时的字段值
            try {
                const saved = localStorage.getItem(UPLOAD_DRAWER_LAST_VALUES_KEY);
                if (saved) {
                    const lastValues = JSON.parse(saved);
                    form.setFieldsValue({
                        supplier: lastValues.supplier ?? undefined,
                        cost_type: lastValues.cost_type ?? undefined,
                        airport_code: lastValues.airport_code ?? undefined,
                        batch_number: lastValues.batch_number ?? undefined,
                    });
                }
            } catch (e) {
                console.warn("恢复上次上传表单值失败:", e);
            }
        }
    }, [open]);

    const loadSuppliers = async () => {
        setLoadingSuppliers(true);
        try {
            const response = await businessPartnerApi.getSuppliers({
                partner_type: "SUPPLIER",
                is_active: true,
                page: 1,
                page_size: 500, // 后端约束：<= 500
            });

            if (response.data && Array.isArray(response.data.items)) {
                setSuppliers(
                    response.data.items.map((supplier) => ({
                        value: supplier.id,
                        label: supplier.code ? `${supplier.name} (${supplier.code})` : supplier.name,
                        code: supplier.code,
                    })),
                );
            }
        } catch (error) {
            console.error("加载供应商列表失败:", error);
            message.error("加载供应商列表失败");
        } finally {
            setLoadingSuppliers(false);
        }
    };

    // V4.9 新增：加载合作方（CUSTOMER 类型）列表
    const loadCustomers = async () => {
        setLoadingCustomers(true);
        try {
            // 注意：/customers 端点直接返回数组，非分页格式
            const response = await businessPartnerApi.getCustomers({
                is_active: true,
            });
            const list = Array.isArray(response.data) ? response.data : (response.data?.items || []);
            setCustomers(
                list.map((c) => ({
                    value: c.id,
                    label: c.code ? `${c.name} (${c.code})` : c.name,
                })),
            );
        } catch (error) {
            console.error("加载合作方列表失败:", error);
        } finally {
            setLoadingCustomers(false);
        }
    };

    // ========== V4.4 新增：本地解析PDF页数工具函数 ==========
    /**
     * 本地解析PDF文件获取页数
     * @param {File} file - 要解析的File对象
     * @returns {Promise<number|null>} - 页数，解析失败返回null
     */
    const getPdfPageCountLocal = async (file) => {
        if (!file || (!file.type.includes("pdf") && !file.name.toLowerCase().endsWith(".pdf"))) {
            return null;
        }
        try {
            // pdfjs 可以直接读取 File/Blob 对象
            const arrayBuffer = await file.arrayBuffer();
            const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
            const pdf = await loadingTask.promise;
            console.log(`[PDF本地解析] ${file.name} 共 ${pdf.numPages} 页`);
            return pdf.numPages;
        } catch (error) {
            console.error(`[PDF本地解析] 解析失败 ${file.name}:`, error);
            return null;
        }
    };

    // ========== V4.6 新增：本地解析 Word 文件字符数工具函数 ==========
    /**
     * 本地解析 Word 文件获取字符数
     * 使用 mammoth 提取文本内容并计算字符数
     * @param {File} file - 要解析的 Word File 对象
     * @returns {Promise<number|null>} - 字符数，解析失败返回 null
     */
    const getWordCharCountLocal = async (file) => {
        const fileName = file.name.toLowerCase();
        if (!fileName.endsWith(".docx") && !fileName.endsWith(".doc")) {
            return null;
        }
        try {
            const arrayBuffer = await file.arrayBuffer();
            // mammoth.extractRawText 专门用于提取纯文本
            const result = await mammoth.extractRawText({ arrayBuffer });
            const charCount = result.value.length;
            console.log(`[Word本地解析] ${file.name} 共 ${charCount} 字符`);
            return charCount;
        } catch (error) {
            console.error(`[Word本地解析] 解析失败 ${file.name}:`, error);
            // .doc 格式可能不被 mammoth 支持，返回 null 表示无法检查
            return null;
        }
    };

    // ========== V4.5 系统化重构：批量文件预检查 ==========
    /**
     * 批量预检查文件（本地 + 后端）
     * @param {File[]} files - 要检查的文件列表
     * @returns {Promise<{passed: Array, blocked: Array, warnings: Array, needsSplit: Array}>}
     */
    const batchPreCheckFiles = async (files) => {
        const maxSize = 60 * 1024 * 1024; // 60MB
        // V4.6: 添加 Word 格式支持 (.docx)
        // 注意：.xls（旧版二进制）和 .doc（旧版二进制）不被后端库支持（openpyxl / python-docx），因此不允许上传
        const allowedExtensions = [".pdf", ".jpg", ".jpeg", ".png", ".xlsx", ".docx"];

        const passed = []; // 可以上传
        const blocked = []; // 阻止上传（格式错误、太大）
        const warnings = []; // 警告但允许（重复、页数>5）
        const needsSplit = []; // 需要拆分（页数>10）

        for (const file of files) {
            const fileName = file.name.toLowerCase();
            const extension = fileName.substring(fileName.lastIndexOf("."));
            const checkResult = { file, issues: [] };

            // ========== 本地校验 ==========

            // 1. 格式校验
            if (!allowedExtensions.includes(extension)) {
                checkResult.issues.push({ type: "FORMAT_ERROR", message: "不支持的文件格式" });
                blocked.push(checkResult);
                continue;
            }

            // 2. 大小校验
            if (file.size > maxSize) {
                checkResult.issues.push({ type: "SIZE_ERROR", message: "文件超过 60MB 限制" });
                blocked.push(checkResult);
                continue;
            }

            // 3. PDF页数校验（本地解析）
            let pageCount = null;
            if (file.type === "application/pdf" || extension === ".pdf") {
                pageCount = await getPdfPageCountLocal(file);
                if (pageCount !== null) {
                    pendingPageCountRef.current[file.name] = pageCount;
                    checkResult.pageCount = pageCount;

                    if (pageCount > 10) {
                        // 超过10页：需要拆分（不是blocked，用户可以选择拆分）
                        checkResult.issues.push({
                            type: "PAGE_LIMIT_NEEDS_SPLIT",
                            message: `PDF共 ${pageCount} 页，超过10页限制，需要拆分`,
                        });
                        needsSplit.push(checkResult);
                        continue;
                    } else if (pageCount > 5) {
                        checkResult.issues.push({
                            type: "PAGE_WARNING",
                            message: `PDF共 ${pageCount} 页，建议拆分后上传`,
                        });
                    }
                }
            }

            // 4. Word 字符数校验（V4.6 新增）
            if (extension === ".docx") {
                const charCount = await getWordCharCountLocal(file);
                if (charCount !== null) {
                    checkResult.charCount = charCount;

                    if (charCount > WORD_CHAR_LIMIT_HARD) {
                        // 超过硬限制：阻止上传
                        checkResult.issues.push({
                            type: "CHAR_LIMIT_ERROR",
                            message: `Word 文档共 ${charCount.toLocaleString()} 字符，超过 ${WORD_CHAR_LIMIT_HARD.toLocaleString()} 字符限制，请拆分后上传`,
                        });
                        blocked.push(checkResult);
                        continue;
                    } else if (charCount > WORD_CHAR_LIMIT_SOFT) {
                        // 超过软限制：警告
                        checkResult.issues.push({
                            type: "CHAR_WARNING",
                            message: `Word 文档共 ${charCount.toLocaleString()} 字符，建议拆分后上传`,
                        });
                    }
                }
            }

            // ========== 后端校验 ==========

            // 4. 文件重复检查
            try {
                const response = await settlementService.checkDuplicateFile(file.name);
                if (response.data && response.data.exists) {
                    checkResult.issues.push({
                        type: "DUPLICATE_WARNING",
                        message: "文件名已存在于系统中",
                        duplicates: response.data.duplicates,
                    });
                }
            } catch (error) {
                console.warn(`检查文件重复失败 (${file.name}):`, error);
            }

            // 分类结果
            if (checkResult.issues.some((i) => i.type.includes("ERROR"))) {
                blocked.push(checkResult);
            } else if (checkResult.issues.length > 0) {
                warnings.push(checkResult);
            } else {
                passed.push(checkResult);
            }
        }
        console.log("[batchPreCheckFiles] 检查结果:", {
            passed: passed.length,
            warnings: warnings.length,
            blocked: blocked.length,
            needsSplit: needsSplit.length,
            details: { passed, warnings, blocked, needsSplit },
        });

        return { passed, blocked, warnings, needsSplit };
    };

    /**
     * 显示批量预检查结果汇总（使用受控Modal）
     * @returns {Promise<{proceed: boolean, filesToUpload: File[]}>}
     */
    const showBatchCheckSummary = (checkResult) => {
        const { passed, blocked, warnings, needsSplit } = checkResult;
        const totalUploadable = passed.length + warnings.length;

        return new Promise((resolve) => {
            // 如果全部通过且无警告，直接返回
            if (blocked.length === 0 && warnings.length === 0 && needsSplit.length === 0) {
                resolve({ proceed: true, filesToUpload: passed.map((r) => r.file), filesToSplit: [] });
                return;
            }

            // 如果没有可上传的文件，且有需要拆分的文件 - 打开受控Modal
            if (totalUploadable === 0 && needsSplit.length > 0) {
                batchCheckResolveRef.current = resolve;
                setBatchCheckResult(checkResult);
                setBatchCheckModalVisible(true);
                return;
            }

            // 如果没有任何可处理的文件
            if (totalUploadable === 0 && needsSplit.length === 0) {
                Modal.error({
                    title: "无法上传",
                    content: (
                        <div>
                            <p>所有文件都无法上传：</p>
                            <ul style={{ paddingLeft: 20, maxHeight: 300, overflowY: "auto" }}>
                                {blocked.map((r, idx) => (
                                    <li key={idx} style={{ marginBottom: 8 }}>
                                        <b>{r.file.name}</b>
                                        <ul style={{ paddingLeft: 16, marginTop: 4 }}>
                                            {r.issues.map((issue, i) => (
                                                <li key={i} style={{ color: "#ff4d4f" }}>
                                                    {issue.message}
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ),
                    okText: "知道了",
                    onOk: () => resolve({ proceed: false, filesToUpload: [], filesToSplit: [] }),
                });
                return;
            }

            // 有可上传的文件或需要拆分的文件 - 打开受控Modal
            batchCheckResolveRef.current = resolve;
            setBatchCheckResult(checkResult);
            setBatchCheckModalVisible(true);
        });
    };

    // ========== 批量检查Modal处理函数 ==========

    // 关闭批量检查Modal
    const closeBatchCheckModal = () => {
        setBatchCheckModalVisible(false);
        setBatchCheckResult(null);
        batchCheckResolveRef.current = null;
    };

    // 批量检查Modal：继续上传
    const handleBatchCheckProceed = () => {
        if (batchCheckResult && batchCheckResolveRef.current) {
            const { passed, warnings } = batchCheckResult;
            const filesToUpload = [...passed, ...warnings].map((r) => r.file);
            batchCheckResolveRef.current({ proceed: true, filesToUpload });
        }
        closeBatchCheckModal();
    };

    // 批量检查Modal：取消
    const handleBatchCheckCancel = () => {
        if (batchCheckResolveRef.current) {
            batchCheckResolveRef.current({ proceed: false, filesToUpload: [] });
        }
        closeBatchCheckModal();
    };

    // 批量检查Modal：拆分某个文件
    const handleSplitFromBatchCheck = (fileInfo) => {
        // 打开拆分Modal
        handleOpenSplitModal({
            filename: fileInfo.file.name,
            rawFile: fileInfo.file,
            page_count: fileInfo.pageCount,
        });
    };
    const handleBeforeUpload = async (file) => {
        const checkResult = await batchPreCheckFiles([file]);

        // 如果被阻止
        if (checkResult.blocked.length > 0) {
            const issues = checkResult.blocked[0].issues;
            Modal.error({
                title: "无法上传",
                content: (
                    <div>
                        <p>
                            文件 <b>{file.name}</b> 无法上传：
                        </p>
                        <ul style={{ paddingLeft: 20 }}>
                            {issues.map((issue, i) => (
                                <li key={i} style={{ color: "#ff4d4f" }}>
                                    {issue.message}
                                </li>
                            ))}
                        </ul>
                    </div>
                ),
                okText: "知道了",
            });
            return false;
        }

        // 如果需要拆分
        if (checkResult.needsSplit.length > 0) {
            const splitInfo = checkResult.needsSplit[0];
            Modal.confirm({
                title: "文件需要拆分",
                content: (
                    <div>
                        <p>
                            文件 <b>{file.name}</b> 共 <span style={{ color: "#fa8c16", fontWeight: "bold" }}>{splitInfo.pageCount}</span> 页，超过10页限制。
                        </p>
                        <p>请先拆分后再上传。</p>
                    </div>
                ),
                okText: "立即拆分",
                cancelText: "取消",
                onOk: () => {
                    handleOpenSplitModal({
                        filename: file.name,
                        rawFile: file,
                        page_count: splitInfo.pageCount,
                    });
                },
            });
            return false;
        }

        // 如果有警告，静默提示
        if (checkResult.warnings.length > 0) {
            const issues = checkResult.warnings[0].issues;
            message.warning({
                content: `${file.name}: ${issues.map((i) => i.message).join("; ")}`,
                duration: 5,
            });
        }

        return true;
    };

    // 处理单个文件上传成功
    const handleAttachmentAdd = (attachmentData) => {
        // 从pendingPageCountRef中获取页数
        const pageCount = pendingPageCountRef.current[attachmentData.filename];
        const fileDataToAdd = {
            ...attachmentData,
            page_count: pageCount || null,
        };

        // 清理缓存
        if (pageCount !== undefined) {
            delete pendingPageCountRef.current[attachmentData.filename];
        }

        setUploadedFileInfos((prev) => [...prev, fileDataToAdd]);
    };

    // 手动上传文件处理逻辑 (用于拖拽上传)
    const handleManualUpload = async (files) => {
        const fileList = Array.from(files);
        if (fileList.length === 0) return;

        // ========== Phase 1 & 2: 批量预检查 ==========
        const hide = message.loading("正在检查文件...", 0);
        let checkResult;
        try {
            checkResult = await batchPreCheckFiles(fileList);
        } finally {
            hide();
        }

        // ========== Phase 3: 显示汇总结果 ==========
        const { proceed, filesToUpload } = await showBatchCheckSummary(checkResult);

        if (!proceed || filesToUpload.length === 0) {
            return;
        }

        // ========== Phase 4: 执行上传 ==========
        let successCount = 0;
        let failCount = 0;

        for (const file of filesToUpload) {
            const uploadHide = message.loading(`正在上传 "${file.name}" (${successCount + failCount + 1}/${filesToUpload.length})...`, 0);
            try {
                const result = await uploadFile(file, null, true);

                // 从pendingPageCountRef中获取页数
                const pageCount = pendingPageCountRef.current[file.name];
                const attachmentData = {
                    ...result,
                    file_size: file.size,
                    content_type: file.type,
                    page_count: pageCount || null,
                };

                // 清理缓存
                if (pageCount !== undefined) {
                    delete pendingPageCountRef.current[file.name];
                }

                setUploadedFileInfos((prev) => [...prev, attachmentData]);
                successCount++;
            } catch (error) {
                console.error(`上传错误 (${file.name}):`, error);
                failCount++;
            } finally {
                uploadHide();
            }
        }

        // 显示最终结果
        if (failCount === 0) {
            message.success(`成功上传 ${successCount} 个文件`);
        } else {
            message.warning(`上传完成: ${successCount} 成功, ${failCount} 失败`);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragging) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            await handleManualUpload(files);
        }
    };

    // 删除已上传的文件
    const handleRemoveFile = (fileToRemove) => {
        const fileKey = fileToRemove.storage_path;

        // 从文件列表中删除
        setUploadedFileInfos((prev) => prev.filter((file) => file.storage_path !== fileKey));

        // 同时从所有关联组中移除该文件
        setFileGroups((prev) =>
            prev
                .map((group) => ({
                    ...group,
                    memberKeys: group.memberKeys.filter((key) => key !== fileKey),
                }))
                // 如果组内文件少于2个，自动解散该组
                .filter((group) => group.memberKeys.length >= 2),
        );
    };

    // 创建文件关联组
    const handleCreateFileGroup = (groupName, fileKeys) => {
        // 校验：至少2个文件
        if (fileKeys.length < 2) {
            message.warning("关联组至少需要2个文件");
            return;
        }

        // 校验：组名长度
        if (groupName && groupName.length > 200) {
            message.warning("组名最多200个字符");
            return;
        }

        // 校验：文件不能同时属于多个组
        const existingMemberKeys = fileGroups.flatMap((g) => g.memberKeys);
        const duplicateKeys = fileKeys.filter((key) => existingMemberKeys.includes(key));
        if (duplicateKeys.length > 0) {
            message.warning("部分文件已属于其他关联组，请先从原组中移除");
            return;
        }

        const newGroup = {
            id: uuidv4(),
            name: groupName || null,
            memberKeys: fileKeys,
        };

        setFileGroups((prev) => [...prev, newGroup]);
        message.success("已创建文件关联组");
    };

    // 编辑关联组名称
    const handleEditGroupName = (groupId, newName) => {
        // 校验：组名长度
        if (newName && newName.length > 200) {
            message.warning("组名最多200个字符");
            return;
        }

        setFileGroups((prev) => prev.map((group) => (group.id === groupId ? { ...group, name: newName } : group)));
        message.success("已更新组名");
    };

    // 从关联组移除成员
    const handleRemoveFromGroup = (groupId, fileKey) => {
        setFileGroups((prev) =>
            prev
                .map((group) => {
                    if (group.id === groupId) {
                        const newMemberKeys = group.memberKeys.filter((key) => key !== fileKey);
                        // 如果移除后少于2个文件，标记为待删除
                        if (newMemberKeys.length < 2) {
                            message.info("文件已移除，关联组因少于2个成员而自动解散");
                            return null;
                        }
                        return { ...group, memberKeys: newMemberKeys };
                    }
                    return group;
                })
                .filter(Boolean),
        );
    };

    // 解散关联组
    const handleDisbandGroup = (groupId) => {
        setFileGroups((prev) => prev.filter((group) => group.id !== groupId));
        message.success("已解散关联组，文件已恢复为独立状态");
    };

    // 打开拆分弹窗
    const handleOpenSplitModal = (file) => {
        setSplittingFile(file);
        setIsSplitModalVisible(true);
    };

    // 处理拆分完成
    const handleSplitComplete = async (originalFile, subFiles) => {
        try {
            // 获取表单值，用于继承到子文件
            const values = form.getFieldsValue();

            // 1. 标记原始文件为"已被拆分"（用于暂存框显示特殊图标）
            setUploadedFileInfos((prev) => prev.map((file) => (file.storage_path === originalFile.storage_path ? { ...file, has_been_split: true } : file)));
            // 注意：原始文件虽然保留，但后端会将其状态设为RECEIVED，列表页不显示，不会OCR识别

            // 2. 上传拆分后的子文件到OSS
            message.loading({ content: "正在上传拆分后的文件...", key: "uploadingSplit" });

            const uploadedSubFiles = [];
            for (const subFile of subFiles) {
                try {
                    // 使用现有的uploadFile函数上传到OSS
                    const result = await uploadFile(subFile.file);

                    // 创建文件信息对象，包含拆分元数据
                    const fileInfo = {
                        storage_path: result.storage_path,
                        filename: subFile.filename,
                        content_type: result.content_type,
                        file_size: result.file_size,
                        download_url: result.download_url, // 保存下载URL用于预览
                        // 拆分元数据
                        is_split: true,
                        split_from_filename: originalFile.filename,
                        split_pages: subFile.pageRange,
                    };

                    uploadedSubFiles.push(fileInfo);
                } catch (error) {
                    console.error(`上传子文件失败 (${subFile.filename}):`, error);
                    throw error;
                }
            }

            // 3. 将上传成功的子文件添加到文件列表
            setUploadedFileInfos((prev) => [...prev, ...uploadedSubFiles]);

            message.success({
                content: `成功拆分并上传 ${uploadedSubFiles.length} 个文件`,
                key: "uploadingSplit",
                duration: 3,
            });

            // 4. 从批量检查结果中移除该文件
            setBatchCheckResult((prev) => {
                if (!prev) return prev;
                // 注意：originalFile可能是本地 File 对象，也可能是已上传的文件对象
                // 这里我们通过文件名来匹配
                const newNeedsSplit = prev.needsSplit.filter((r) => r.file.name !== (originalFile.filename || originalFile.name));
                return { ...prev, needsSplit: newNeedsSplit };
            });
        } catch (error) {
            console.error("处理拆分文件失败:", error);
            message.error({
                content: "上传拆分文件失败: " + error.message,
                key: "uploadingSplit",
                duration: 5,
            });
        } finally {
            // 关闭当前拆分弹窗
            setIsSplitModalVisible(false);
            setSplittingFile(null);
        }
    };

    // 取消拆分
    const handleCancelSplit = () => {
        setIsSplitModalVisible(false);
        setSplittingFile(null);
    };

    // 提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (uploadedFileInfos.length === 0) {
                message.warning("请至少上传一个文件");
                return;
            }

            // 前端校验：确保所有关联组都有至少2个文件
            const invalidGroups = fileGroups.filter((group) => group.memberKeys.length < 2);
            if (invalidGroups.length > 0) {
                message.error("存在文件数少于2的关联组，请检查");
                return;
            }

            setIsUploading(true);

            // ========== V4.2 新格式：文件组包含完整文件信息 ==========
            // 1. 收集所有在文件组中的文件key
            const groupedFileKeys = new Set();
            fileGroups.forEach((group) => {
                group.memberKeys.forEach((key) => groupedFileKeys.add(key));
            });

            // 2. 构建文件组数据（新格式：包含files字段）
            const file_groups =
                fileGroups.length > 0
                    ? fileGroups.map((group) => {
                          // 为组内每个文件构建完整信息
                          const groupFiles = group.memberKeys
                              .map((key) => {
                                  const fileInfo = uploadedFileInfos.find((f) => f.storage_path === key);
                                  if (!fileInfo) {
                                      console.warn(`文件组中的文件 ${key} 未在上传列表中找到`);
                                      return null;
                                  }
                                  return {
                                      object_key: fileInfo.storage_path,
                                      file_name: fileInfo.filename,
                                      content_type: fileInfo.content_type,
                                      file_size: fileInfo.file_size,
                                      // 拆分元数据（如果是拆分文件）
                                      is_split: fileInfo.is_split || false,
                                      split_from_filename: fileInfo.split_from_filename || null,
                                      split_pages: fileInfo.split_pages || null,
                                  };
                              })
                              .filter(Boolean); // 过滤null值

                          return {
                              group_name: group.name || null,
                              files: groupFiles, // 新格式：包含完整文件信息
                          };
                      })
                    : undefined;

            // 3. 构建独立文件数据（排除文件组中的文件）
            const independentFiles = uploadedFileInfos
                .filter((file) => !groupedFileKeys.has(file.storage_path))
                .map((file) => ({
                    object_key: file.storage_path,
                    file_name: file.filename,
                    content_type: file.content_type,
                    file_size: file.file_size,
                    // 拆分元数据（如果是拆分文件）
                    is_split: file.is_split || false,
                    split_from_filename: file.split_from_filename || null,
                    split_pages: file.split_pages || null,
                }));

            // 4. 构建最终请求数据
            const registerData = {
                vendor_id: values.supplier,
                standard_cost_type: values.cost_type,
                files: independentFiles, // 只包含独立文件
                parse_immediately: isPartnerBilling ? false : values.parse_immediately,
                auto_trigger_settlement: isPartnerBilling ? false : values.auto_trigger_settlement,
                parser_instructions: null,
                batch_number: values.batch_number?.trim() || undefined,
                airport_code: isPartnerBilling ? undefined : (values.airport_code || undefined),
                source_type: uploadMode, // V4.9 新增：来源类型
            };

            // 5. 添加文件组（如果有）
            if (file_groups) {
                registerData.file_groups = file_groups;
            }

            // 📋 调试日志：查看提交数据（V4.2新格式）
            console.log("=== 文件组提交数据（新格式）===");
            console.log("📁 独立文件数:", registerData.files.length);
            console.log("🔗 文件组数:", registerData.file_groups?.length || 0);
            if (registerData.file_groups && registerData.file_groups.length > 0) {
                registerData.file_groups.forEach((group, index) => {
                    console.log(`\n🔗 文件组 ${index + 1}:`, group.group_name || "未命名");
                    console.log("   组内文件数:", group.files.length);
                    console.log("   文件列表:", group.files.map((f) => f.file_name).join(", "));
                });
            }
            console.log("\n📦 完整请求数据:", JSON.stringify(registerData, null, 2));
            console.log("========================\n");

            const registerResult = await settlementService.batchRegisterInvoiceDataSources(registerData);

            // V4.9 合作方账单：上传成功后自动创建 portal_publication 关联记录
            if (isPartnerBilling && values.customer_partner_id) {
                try {
                    const idsIds = (registerResult.data?.data?.items || []).map((item) => item.id).filter(Boolean);
                    if (idsIds.length > 0) {
                        await settlementService.portalPublication.batchCreate({
                            ids_ids: idsIds,
                            customer_partner_id: values.customer_partner_id,
                        });
                        console.log(`自动创建 ${idsIds.length} 条门户发布记录，合作方ID=${values.customer_partner_id}`);
                    }
                } catch (pubError) {
                    console.error("自动创建门户发布记录失败:", pubError);
                    message.warning("文件上传成功，但关联合作方失败，请在列表页手动指定");
                }
            }

            // 提示信息优化
            let successMessage;
            if (isPartnerBilling) {
                successMessage = `成功上传 ${uploadedFileInfos.length} 个合作方账单文件并关联合作方，请在列表页确认后发布`;
            } else if (fileGroups.length > 0) {
                const recordCount = fileGroups.length + independentFiles.length;
                successMessage = `成功上传 ${uploadedFileInfos.length} 个文件，创建 ${recordCount} 条记录（${fileGroups.length} 个文件组 + ${independentFiles.length} 个独立文件），解析已开始`;
            } else {
                successMessage = `成功上传并注册 ${uploadedFileInfos.length} 个账单数据源，解析已开始`;
            }
            message.success(successMessage);

            // 保存本次提交的表单值到 localStorage，以便下次打开抽屉时恢复
            try {
                const valuesToSave = {
                    supplier: values.supplier,
                    cost_type: values.cost_type,
                    airport_code: values.airport_code,
                    batch_number: values.batch_number,
                };
                localStorage.setItem(UPLOAD_DRAWER_LAST_VALUES_KEY, JSON.stringify(valuesToSave));
            } catch (e) {
                console.warn("保存上传表单值失败:", e);
            }

            // 清理
            setUploadedFileInfos([]);
            setFileGroups([]);
            form.resetFields();

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error("提交失败:", error);
            message.error("提交失败: " + (error.response?.data?.detail || error.message));
        } finally {
            setIsUploading(false);
        }
    };

    // 关闭抽屉
    const handleClose = () => {
        if (isUploading) {
            message.warning("正在提交中，请稍候...");
            return;
        }

        // 检查是否有未提交的关联组
        if (fileGroups.length > 0) {
            const groupCount = fileGroups.length;
            message.warning(`您有 ${groupCount} 个未提交的关联组，关闭后将丢失`);
        }

        form.resetFields();
        setUploadedFileInfos([]);
        setFileGroups([]);
        onClose();
    };

    return (
        <Drawer
            title="批量上传账单文件"
            width={720}
            open={open}
            onClose={handleClose}
            maskClosable={!isUploading}
            styles={{ body: { padding: 0 } }} // 移除 body 内边距以便拖拽区域撑满
            footer={
                <div style={{ textAlign: "right" }}>
                    <Space>
                        <Button onClick={handleClose} disabled={isUploading}>
                            取消
                        </Button>
                        <Button type="primary" onClick={handleSubmit} loading={isUploading} disabled={uploadedFileInfos.length === 0}>
                            {isUploading ? "提交中..." : "提交"}
                        </Button>
                    </Space>
                </div>
            }
        >
            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                    position: "relative",
                    minHeight: "100%",
                    padding: "24px",
                    transition: "all 0.3s",
                    backgroundColor: isDragging ? "rgba(24, 144, 255, 0.05)" : "transparent",
                }}
            >
                {/* 拖拽 Overlay */}
                {isDragging && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 100,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(24, 144, 255, 0.1)",
                            border: "2px dashed #1890ff",
                            borderRadius: "4px",
                            pointerEvents: "none",
                        }}
                    >
                        <div style={{ color: "#1890ff", fontSize: "18px", fontWeight: "bold" }}>释放文件以开始上传</div>
                    </div>
                )}

                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        supplier: undefined,
                        cost_type: undefined,
                        parse_immediately: true,
                        auto_trigger_settlement: true,
                    }}
                >
                    {/* V4.9 新增：上传模式切换 */}
                    <div style={{ marginBottom: 16 }}>
                        <Segmented
                            options={[
                                { label: "账单上传", value: "INVOICE_UPLOAD" },
                                { label: "合作方账单", value: "PARTNER_BILLING" },
                            ]}
                            value={uploadMode}
                            onChange={(val) => {
                                setUploadMode(val);
                                // 切换时重置解析相关字段
                                if (val === "PARTNER_BILLING") {
                                    form.setFieldsValue({ parse_immediately: false, auto_trigger_settlement: false });
                                } else {
                                    form.setFieldsValue({ parse_immediately: true, auto_trigger_settlement: true });
                                }
                            }}
                            block
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>
                            来源类型：<Tag color={isPartnerBilling ? "purple" : "blue"}>{isPartnerBilling ? "合作方账单 (PARTNER_BILLING)" : "账单上传 (INVOICE_UPLOAD)"}</Tag>
                        </div>
                    </div>

                    <Alert
                        message={isPartnerBilling ? "合作方账单上传说明" : "批量上传说明"}
                        description={
                            isPartnerBilling
                                ? "上传原始账单文件以便共享给运营合作方审查。选择合作方后系统自动创建发布记录，上传完成后在列表页确认并发布。"
                                : "同一批次上传的文件必须属于相同的供应商和费用类型。可选择上传后自动解析文件，解析成功后可自动触发结算处理流程。"
                        }
                        type={isPartnerBilling ? "warning" : "info"}
                        showIcon
                        style={{ marginBottom: 24 }}
                    />

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="supplier" label="供应商" rules={[{ required: true, message: "请选择供应商" }]}>
                                <Select placeholder="请搜索或选择供应商" showSearch loading={loadingSuppliers} filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={suppliers} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="cost_type" label="费用类型（预分类）" rules={[{ required: true, message: "请选择费用类型" }]}>
                                <Select placeholder="请选择费用类型（预分类）" showSearch options={COST_TYPE_OPTIONS} filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* V4.9 新增：合作方账单模式下显示合作方选择器 */}
                    {isPartnerBilling && (
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="customer_partner_id"
                                    label="合作方"
                                    rules={[{ required: true, message: "请选择合作方" }]}
                                >
                                    <Select
                                        placeholder="请搜索或选择合作方（客户类型）"
                                        showSearch
                                        loading={loadingCustomers}
                                        options={customers}
                                        filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {/* V4.7 新增：批次号输入 + V4.8 新增：关联机场（合作方账单模式下隐藏机场） */}
                    <Row gutter={16}>
                        <Col span={isPartnerBilling ? 24 : 16}>
                            <Form.Item
                                name="batch_number"
                                label={
                                    <span>
                                        批次号
                                        <Tooltip title="留空时系统将自动生成批次号。建议格式：BATCH_YYYYMMDD_HHMMSS">
                                            <InfoCircleOutlined style={{ marginLeft: 4, color: "#1890ff" }} />
                                        </Tooltip>
                                    </span>
                                }
                            >
                                <Input
                                    placeholder="请输入批次号（留空自动生成）"
                                    addonAfter={
                                        <Button type="link" size="small" onClick={() => form.setFieldsValue({ batch_number: generateRecommendedBatchNumber() })} style={{ padding: 0, height: "auto" }}>
                                            推荐
                                        </Button>
                                    }
                                    maxLength={50}
                                />
                            </Form.Item>
                        </Col>
                        {!isPartnerBilling && (
                            <Col span={8}>
                                <Form.Item
                                    name="airport_code"
                                    label={
                                        <span>
                                            关联机场
                                            <Tooltip title="可选，选择与该批次文件关联的机场">
                                                <InfoCircleOutlined style={{ marginLeft: 4, color: "#1890ff" }} />
                                            </Tooltip>
                                        </span>
                                    }
                                >
                                    <AirportSelector
                                        valueType="iata_code"
                                        placeholder="选择机场（可选）"
                                        allowClear
                                        style={{ width: "100%" }}
                                    />
                                </Form.Item>
                            </Col>
                        )}
                    </Row>

                    {!isPartnerBilling && (
                        <>
                            <Form.Item name="parse_immediately" valuePropName="checked">
                                <Checkbox>上传后自动解析文件</Checkbox>
                            </Form.Item>

                            <Form.Item name="auto_trigger_settlement" valuePropName="checked">
                                <Checkbox>解析成功后自动执行结算处理</Checkbox>
                            </Form.Item>
                        </>
                    )}

                    <Form.Item label="上传文件">
                        <div
                            style={{
                                padding: "16px",
                                background: "#fafafa",
                                border: "1px dashed #d9d9d9",
                                borderRadius: "8px",
                                transition: "all 0.3s",
                            }}
                            onMouseOver={(e) => (e.currentTarget.style.borderColor = "#1890ff")}
                            onMouseOut={(e) => (e.currentTarget.style.borderColor = "#d9d9d9")}
                        >
                            <AttachmentUpload onAttachmentAdd={handleAttachmentAdd} multiple={true} accept=".pdf,.jpg,.jpeg,.png,.xlsx,.docx" maxSize={60 * 1024 * 1024} onFilesSelected={handleManualUpload} isTemporary={true} hint="直接点击上方按钮或拖拽文件到抽屉任意位置上传（支持 PDF、Excel(.xlsx)、Word(.docx)、图片，单个文件最大 60MB）" />
                        </div>
                    </Form.Item>

                    {/* 使用可编辑文件列表组件 */}
                    <EditableFileList uploadedFiles={uploadedFileInfos} fileGroups={fileGroups} onCreateGroup={handleCreateFileGroup} onDeleteFile={handleRemoveFile} onRemoveMemberFromGroup={handleRemoveFromGroup} onDisbandGroup={handleDisbandGroup} onEditGroupName={handleEditGroupName} onSplitFile={handleOpenSplitModal} isSubmitting={isUploading} />
                </Form>
            </div>

            {/* PDF拆分弹窗 */}
            <FileSplitModal open={isSplitModalVisible} file={splittingFile} onConfirm={handleSplitComplete} onCancel={handleCancelSplit} />

            {/* 批量检查结果Modal */}
            <Modal
                title="文件检查结果"
                open={batchCheckModalVisible}
                width={650}
                onCancel={handleBatchCheckCancel}
                footer={
                    <Space>
                        <Button onClick={handleBatchCheckCancel}>取消全部</Button>
                        {batchCheckResult && batchCheckResult.passed.length + batchCheckResult.warnings.length > 0 && (
                            <Button type="primary" onClick={handleBatchCheckProceed}>
                                继续上传 {batchCheckResult.passed.length + batchCheckResult.warnings.length} 个文件
                            </Button>
                        )}
                    </Space>
                }
            >
                {batchCheckResult && (
                    <div style={{ maxHeight: 450, overflowY: "auto" }}>
                        {/* 可上传文件 */}
                        {batchCheckResult.passed.length + batchCheckResult.warnings.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ fontWeight: 500, color: "#52c41a", marginBottom: 8 }}>✅ 可上传: {batchCheckResult.passed.length + batchCheckResult.warnings.length} 个文件</div>
                                <ul style={{ paddingLeft: 20, margin: 0 }}>
                                    {batchCheckResult.passed.map((r, idx) => (
                                        <li key={`p-${idx}`} style={{ color: "#52c41a", marginBottom: 4 }}>
                                            {r.file.name} {r.pageCount ? `(${r.pageCount}页)` : r.charCount ? `(${r.charCount.toLocaleString()}字符)` : ""}
                                        </li>
                                    ))}
                                    {batchCheckResult.warnings.map((r, idx) => (
                                        <li key={`w-${idx}`} style={{ color: "#faad14", marginBottom: 4 }}>
                                            {r.file.name} {r.pageCount ? `(${r.pageCount}页)` : r.charCount ? `(${r.charCount.toLocaleString()}字符)` : ""}
                                            <span style={{ fontSize: 12, marginLeft: 8 }}>⚠️ {r.issues.map((i) => i.message).join("; ")}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* 需要拆分的文件 - 带拆分按钮 */}
                        {batchCheckResult.needsSplit.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ fontWeight: 500, color: "#fa8c16", marginBottom: 8 }}>✂️ 需要拆分: {batchCheckResult.needsSplit.length} 个文件</div>
                                <div style={{ paddingLeft: 20 }}>
                                    {batchCheckResult.needsSplit.map((r, idx) => (
                                        <div
                                            key={idx}
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                padding: "8px 12px",
                                                marginBottom: 8,
                                                background: "#fff7e6",
                                                borderRadius: 4,
                                                border: "1px solid #ffd591",
                                            }}
                                        >
                                            <span style={{ color: "#fa8c16" }}>
                                                {r.file.name} <Tag color="orange">{r.pageCount}页</Tag>
                                            </span>
                                            <Button type="primary" size="small" onClick={() => handleSplitFromBatchCheck(r)}>
                                                拆分
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                                <p style={{ fontSize: 12, color: "#666", marginTop: 8, paddingLeft: 20 }}>点击"拆分"按钮处理单个文件，拆分后的子文件会自动上传。</p>
                            </div>
                        )}

                        {/* 阻止上传的文件 */}
                        {batchCheckResult.blocked.length > 0 && (
                            <div>
                                <div style={{ fontWeight: 500, color: "#ff4d4f", marginBottom: 8 }}>❌ 无法上传: {batchCheckResult.blocked.length} 个文件</div>
                                <ul style={{ paddingLeft: 20, margin: 0 }}>
                                    {batchCheckResult.blocked.map((r, idx) => (
                                        <li key={idx} style={{ color: "#ff4d4f", marginBottom: 4 }}>
                                            {r.file.name}
                                            <span style={{ fontSize: 12, marginLeft: 8 }}>{r.issues.map((i) => i.message).join("; ")}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </Drawer>
    );
};

export default InvoiceDataSourceUploadDrawer;
